'use client';

import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery } from '@tanstack/react-query';

import {
  type AttemptActivity,
  listAttemptActivities,
} from '@/lib/admin/client';
import { attemptActivitiesQueryKey } from '@/lib/admin/queryKeys';

export function useAttemptActivities(
  attemptId: string,
  includePayload: boolean
) {
  const { getToken, isLoaded, isSignedIn, sessionId, userId } = useAuth();
  const ready = isLoaded && !!isSignedIn && !!userId && !!sessionId;

  return useInfiniteQuery({
    queryKey: attemptActivitiesQueryKey(
      { userId, sessionId },
      attemptId,
      includePayload
    ),
    enabled: ready && attemptId.length > 0,
    retry: false,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      if (!token) throw new Error('Missing auth token');
      return listAttemptActivities(token, attemptId, pageParam, includePayload);
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
  });
}

export function attemptActivityPages(
  pages: Array<{ activities: AttemptActivity[] }> | undefined
): AttemptActivity[] {
  return pages?.flatMap((page) => page.activities) ?? [];
}
